import React, { useState } from 'react';
import { Layers, Download, Info, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика накоплений по вкладу
 */
const DepositSchedule = ({ 
  schedule, 
  results, 
  formData, 
  showFullSchedule, 
  setShowFullSchedule,
  currencyOptions
}) => {
  const [activeTab, setActiveTab] = useState('general');
  
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первого года, если не показан полный график
  const displaySchedule = showFullSchedule ? schedule : schedule.slice(0, 12);

  // Получаем символ валюты
  const currency = currencyOptions.find(c => c.id === formData.currency);
  const currencySymbol = currency ? currency.symbol : '';

  // Форматирование денежных значений
  const formatMoney = (value) => {
    return `${formatNumber(value, 2, '.', ' ')} ${currencySymbol}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-green-500" />
          График накоплений ({schedule.length} мес.)
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {schedule.length > 12 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать все ${schedule.length} мес.`}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'general'
              ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Общий график
        </button>
        <button
          onClick={() => setActiveTab('detailed')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'detailed'
              ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Детализация операций
        </button>
        <button
          onClick={() => setActiveTab('interest')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'interest'
              ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500 dark:border-green-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Начисление процентов
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>{showFullSchedule 
            ? "Полный график накоплений по вкладу. Прокрутите таблицу вправо для просмотра всех данных." 
            : `Показаны первые ${displaySchedule.length} месяцев графика накоплений. Нажмите "Показать все месяцы" для просмотра полного графика.`
          }</p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] deposit-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">№</th>
              <th scope="col" className="px-3 py-2 font-semibold">Дата</th>

              {activeTab === 'general' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток на начало</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Операции</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток на конец</th>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток на начало</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Пополнение</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Снятие</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток на конец</th>
                </>
              )}
              
              {activeTab === 'interest' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток на начало</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Начисленные проценты</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Капитализировано</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Всего процентов</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток на конец</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displaySchedule.map((item) => (
              <tr
                key={item.month}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                  ${item.contribution > 0 ? 'bg-green-50 dark:bg-green-900/20' : ''}
                  ${item.withdrawal > 0 ? 'bg-orange-50 dark:bg-orange-900/20' : ''}`}
              >
                <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.month}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </td>

                {activeTab === 'general' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatMoney(item.startingBalance)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {item.contribution > 0 && (
                        <span className="text-green-600 dark:text-green-400">+{formatMoney(item.contribution)}</span>
                      )}
                      {item.withdrawal > 0 && (
                        <span className="text-orange-600 dark:text-orange-400">-{formatMoney(item.withdrawal)}</span>
                      )}
                      {item.contribution === 0 && item.withdrawal === 0 && '-'}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">{formatMoney(item.interest)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatMoney(item.endingBalance)}
                    </td>
                  </>
                )}

                {activeTab === 'detailed' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatMoney(item.startingBalance)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {item.contribution > 0 ? (
                        <span className="text-green-600 dark:text-green-400">{formatMoney(item.contribution)}</span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {item.withdrawal > 0 ? (
                        <span className="text-orange-600 dark:text-orange-400">{formatMoney(item.withdrawal)}</span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">{formatMoney(item.interest)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatMoney(item.endingBalance)}
                    </td>
                  </>
                )}
                
                {activeTab === 'interest' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatMoney(item.startingBalance)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">{formatMoney(item.interest)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {item.capitalizedInterest > 0 ? (
                        <span className="text-green-600 dark:text-green-400">{formatMoney(item.capitalizedInterest)}</span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-blue-700 dark:text-blue-300">
                      {formatMoney(item.totalInterestToDate)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatMoney(item.endingBalance)}
                    </td>
                  </>
                )}
              </tr>
            ))}

            {/* Итоговая строка */}
            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap text-center" colSpan="2">Итого:</td>

              {activeTab === 'general' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatMoney(results.initialAmount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <span className="text-green-600 dark:text-green-400">+{formatMoney(results.totalContributions)}</span>
                    {results.totalWithdrawals > 0 && (
                      <span className="ml-1 text-orange-600 dark:text-orange-400">-{formatMoney(results.totalWithdrawals)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">{formatMoney(results.interestAmount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatMoney(results.finalAmount)}</td>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatMoney(results.initialAmount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">{formatMoney(results.totalContributions)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-orange-600 dark:text-orange-400">{formatMoney(results.totalWithdrawals)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">{formatMoney(results.interestAmount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatMoney(results.finalAmount)}</td>
                </>
              )}
              
              {activeTab === 'interest' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatMoney(results.initialAmount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-blue-600 dark:text-blue-400">{formatMoney(results.interestAmount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                    {results.capitalization !== 'none' ? formatMoney(results.interestAmount) : '-'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-blue-700 dark:text-blue-300">{formatMoney(results.interestAmount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatMoney(results.finalAmount)}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && schedule.length > 12 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} месяцев накопления
          </Button>
        </div>
      )}
      
      {/* Визуализация графика */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center mb-4">
          <BarChart2 className="w-5 h-5 text-green-500 mr-2" />
          <h4 className="text-base font-medium text-gray-900 dark:text-white">
            Динамика накоплений
          </h4>
        </div>
        
        <div className="h-[200px] relative">
          {displaySchedule.map((item, index) => {
            const maxBalance = Math.max(...displaySchedule.map(s => s.endingBalance));
            const height = (item.endingBalance / maxBalance) * 100;
            const width = 100 / displaySchedule.length;
            const left = index * width;
            
            return (
              <div 
                key={item.month}
                className="absolute bottom-0 bg-gradient-to-t from-green-500 to-green-300 dark:from-green-600 dark:to-green-400 rounded-t"
                style={{
                  left: `${left}%`,
                  width: `${width - 2}%`,
                  height: `${height}%`,
                  minHeight: '1%'
                }}
                title={`Месяц ${item.month}: ${formatMoney(item.endingBalance)}`}
              />
            );
          })}
          
          {/* Горизонтальная ось */}
          <div className="absolute bottom-0 w-full h-[1px] bg-gray-300 dark:bg-gray-600"></div>
        </div>
        
        <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
          <div>Месяц 1</div>
          <div>Месяц {displaySchedule.length}</div>
        </div>
      </div>
    </div>
  );
};

export default DepositSchedule;